// Staff Payments — create emailable Stripe payment links + view transactions
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Link2,
  Copy,
  Loader2,
  CreditCard,
  CheckCircle2,
  Clock,
  RefreshCw,
} from "lucide-react";

const money = (a, c) =>
  `${c === "USD" ? "$" : "€"}${Number(a || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function StaffPaymentsPage() {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [form, setForm] = useState({
    amount: "",
    currency: "eur",
    description: "",
    ref_type: "custom",
    pf_number: "",
    customer_email: "",
  });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [links, setLinks] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [l, t] = await Promise.all([
        axios.get(`${API_URL}/payments/links`, auth),
        axios.get(`${API_URL}/payments/transactions`, auth),
      ]);
      setLinks(l.data.links || []);
      setTxns(t.data.transactions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payments");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Enter a description");
      return;
    }
    setCreating(true);
    setCreated(null);
    try {
      const res = await axios.post(
        `${API_URL}/payments/links`,
        {
          amount: Number(form.amount),
          currency: form.currency,
          description: form.description.trim(),
          ref_type: form.ref_type,
          pf_number: form.pf_number || null,
          customer_email: form.customer_email || null,
        },
        auth
      );
      setCreated(res.data);
      toast.success("Payment link created");
      setForm((f) => ({ ...f, amount: "", description: "", pf_number: "", customer_email: "" }));
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Link copied");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="staff-payments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-indigo-700" /> Payments
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Create a Stripe payment link for an invoice, quote or custom amount and email it to a
            customer. EUR &amp; USD, one-time.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="payments-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Create form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-600" /> New payment link
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="125.00"
                  data-testid="payments-amount"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <select
                  className="w-full h-10 border rounded-md px-2 text-sm"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  data-testid="payments-currency"
                >
                  <option value="eur">EUR</option>
                  <option value="usd">USD</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Freight invoice INV-2026-… / Booking deposit"
                data-testid="payments-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>For</Label>
                <select
                  className="w-full h-10 border rounded-md px-2 text-sm"
                  value={form.ref_type}
                  onChange={(e) => setForm({ ...form, ref_type: e.target.value })}
                  data-testid="payments-reftype"
                >
                  <option value="custom">Custom amount</option>
                  <option value="invoice">Invoice</option>
                  <option value="quote">Quote</option>
                  <option value="offer">Offer</option>
                </select>
              </div>
              <div>
                <Label>PF number (optional)</Label>
                <Input
                  value={form.pf_number}
                  onChange={(e) => setForm({ ...form, pf_number: e.target.value })}
                  placeholder="PF-000492-26"
                  data-testid="payments-pf"
                />
              </div>
            </div>
            <div>
              <Label>Customer email (optional — for receipt)</Label>
              <Input
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                placeholder="customer@example.com"
                data-testid="payments-email"
              />
            </div>
            <Button
              type="submit"
              disabled={creating}
              className="w-full bg-indigo-700 hover:bg-indigo-800"
              data-testid="payments-create-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating…
                </>
              ) : (
                "Create payment link"
              )}
            </Button>
          </form>

          {created && (
            <div
              className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200"
              data-testid="payments-created"
            >
              <p className="text-sm font-medium text-emerald-800">
                Link ready — {money(created.amount, created.currency)}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border rounded px-2 py-2 break-all">
                  {created.pay_url}
                </code>
                <Button size="sm" variant="outline" onClick={() => copy(created.pay_url)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-2 text-xs text-emerald-700">
                Paste this into an email to your customer. They can pay without logging in.
              </p>
            </div>
          )}
        </div>

        {/* Links + transactions */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Recent links</h2>
            {loading ? (
              <div className="py-6 text-center text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              </div>
            ) : links.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">No payment links yet.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-auto" data-testid="payments-links-list">
                {links.map((l) => (
                  <div
                    key={l.token}
                    className="flex items-center justify-between gap-2 border-b last:border-0 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {l.description}
                      </p>
                      <p className="text-xs text-slate-400">
                        {money(l.amount, (l.currency || "eur").toUpperCase())}
                        {l.pf_number ? ` · ${l.pf_number}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          l.status === "paid"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {l.status}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          copy(`${window.location.origin}/pay/${l.token}`)
                        }
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Transactions</h2>
            {loading ? (
              <div className="py-6 text-center text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              </div>
            ) : txns.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">No transactions yet.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-auto" data-testid="payments-txn-list">
                {txns.map((t) => (
                  <div
                    key={t.session_id}
                    className="flex items-center justify-between gap-2 border-b last:border-0 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {t.description}
                      </p>
                      <p className="text-xs text-slate-400">
                        {money(t.amount, (t.currency || "eur").toUpperCase())} ·{" "}
                        {t.ref_type}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0 ${
                        t.payment_status === "paid"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {t.payment_status === "paid" ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <Clock className="h-3 w-3" />
                      )}
                      {t.payment_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
